import type { DocumentKind, JobStageTag, Technology } from "@sts/doc-catalogue";
import type {
  DocumentStatus,
  DocumentType,
  EmailSendMode,
  EnvelopeStatus,
  ListedDocument,
  SignatureMode,
} from "../api/types.js";
import { humanise } from "./format.js";

/**
 * The vocabulary of the document screens.
 *
 * Every list here is a `Record` over a union rather than an array of strings,
 * and the arrays are derived from the records with `Object.keys`. That is not
 * decoration: a `Record<DocumentKind, string>` is checked for completeness by
 * the compiler, so a value added to the catalogue cannot quietly go missing
 * from a filter, whereas a hand-written array can and does. Declaration order
 * is the order a person reads them in.
 *
 * Every lookup falls back to the raw value. These unions are transcribed from
 * the API and the catalogue by hand, and a screen printing `undefined` because
 * the database learned a new enum member is a worse failure than one printing
 * the member's own name.
 */

export const DOCUMENT_TYPE_LABEL: Record<DocumentType, string> = {
  "performance-estimate": "Performance estimate",
  quotation: "Quotation",
  contract: "Contract",
  "customer-commitment": "Customer commitment",
  "heat-loss-report": "Heat loss report",
  "design-pack": "Design pack",
  "noise-assessment": "Noise assessment",
  "commissioning-checklist": "Commissioning checklist",
  "handover-checklist": "Handover checklist",
  "dno-notification": "DNO notification",
  "bus-evidence-bundle": "BUS evidence bundle",
  "mcs-certificate": "MCS certificate",
  "installer-pack": "Installer pack",
};

export function documentTypeLabel(type: string): string {
  return DOCUMENT_TYPE_LABEL[type as DocumentType] ?? humanise(type);
}

/**
 * What each state means for the person looking at it.
 *
 * "Superseded" is the one that has to survive being skim-read. A superseded
 * document is still issued and still immutable, so a screen that shows only
 * "Issued" is technically true and practically dangerous — it invites somebody
 * to send an old version to a customer. The word and the sentence both say so.
 */
export const DOCUMENT_STATUS: Record<
  DocumentStatus,
  { readonly word: string; readonly meaning: string }
> = {
  draft: {
    word: "Draft",
    meaning:
      "Still editable, and not evidence. The API refuses to send a draft, so that what a recipient receives is what the vault holds.",
  },
  issued: {
    word: "Issued",
    meaning:
      "Fixed as evidence. The database refuses any change to its bytes from here on.",
  },
  superseded: {
    word: "Superseded",
    meaning:
      "Replaced by a newer version. Still held as evidence of what was given at the time, but no longer the current document — do not send it.",
  },
};

export function documentStatusWord(status: string): string {
  return DOCUMENT_STATUS[status as DocumentStatus]?.word ?? humanise(status);
}

// ------------------------------------------------------------ versions

/** "version 2", or nothing to say when the API did not number it. */
export function versionWord(doc: Pick<ListedDocument, "version">): string | null {
  return typeof doc.version === "number" ? `version ${doc.version}` : null;
}

/**
 * Where a superseded document's line ends: the version that replaced it, and
 * the one that is current now — which is not always the same document, since
 * version 1 superseded by 2 superseded by 3 is still pointed at 3.
 *
 * Read from the job's own list (the vault's `superseded_by` pointers), never
 * guessed from dates. `null` for a document nothing has superseded. A pointer
 * to a document the list does not hold (it should not happen: they are on the
 * same job) leaves `successor`/`current` null rather than inventing one; a
 * loop in the pointers (it cannot happen either) stops where it would repeat.
 */
export interface Supersession {
  readonly successor: ListedDocument | null;
  readonly current: ListedDocument | null;
}

export function supersessionOf(
  doc: ListedDocument,
  all: readonly ListedDocument[],
): Supersession | null {
  if (doc.supersededBy === null) return null;
  const byId = new Map(all.map((d) => [d.id, d]));
  const successor = byId.get(doc.supersededBy) ?? null;
  let current: ListedDocument | null = successor;
  const seen = new Set<string>([doc.id]);
  while (current && current.supersededBy !== null && !seen.has(current.id)) {
    seen.add(current.id);
    current = byId.get(current.supersededBy) ?? null;
  }
  return { successor, current: current && current.supersededBy === null ? current : null };
}

/**
 * "Superseded by version 2, produced 11 September 2026." — the one line a
 * reader of the old document needs. The date is when the successor was
 * produced (the API records no separate issue date).
 */
export function supersededLine(
  doc: ListedDocument,
  all: readonly ListedDocument[],
  formatDate: (iso: string) => string,
): string | null {
  const found = supersessionOf(doc, all);
  if (!found) return null;
  const next = found.successor;
  if (!next) return "Superseded by a newer version.";
  const which = versionWord(next);
  return `Superseded by ${which ?? "a newer version"}, produced ${formatDate(next.createdAt)}.`;
}

/**
 * The current version of each type the job holds a superseded version of —
 * the newest issued document of that type that nothing supersedes. What an
 * installer opening the documents should be pointed at first.
 *
 * "Newest" is the list's own order (`GET /api/jobs/:id/documents` is newest
 * first, ordered on the timestamp itself); the rendered `createdAt` text is
 * not compared here, since text order and time order part at a clock change.
 */
export function currentVersions(all: readonly ListedDocument[]): ListedDocument[] {
  const withHistory = new Set(all.filter((d) => d.status === "superseded").map((d) => d.type));
  const out: ListedDocument[] = [];
  const done = new Set<string>();
  for (const d of all) {
    if (!withHistory.has(d.type) || done.has(d.type) || d.status !== "issued") continue;
    done.add(d.type);
    out.push(d);
  }
  return out;
}

// ------------------------------------------------------------------ finder

export const KIND_LABEL: Record<DocumentKind, string> = {
  generated: "Generated per job",
  form: "Form to fill in",
  procedure: "Company procedure",
  record: "Company record",
  external: "External standard",
};

export const STAGE_TAG_LABEL: Record<JobStageTag, string> = {
  enquiry: "Enquiry",
  survey: "Survey",
  design: "Design",
  quote: "Quote",
  contract: "Contract",
  install: "Install",
  commission: "Commission",
  handover: "Handover",
  aftercare: "Aftercare",
  company: "Company-wide",
};

export const TECHNOLOGY_LABEL: Record<Technology, string> = {
  "heat-pump": "Heat pump",
  "solar-pv": "Solar PV",
  battery: "Battery",
  retrofit: "Retrofit (PAS 2035)",
  any: "Any technology",
};

export const DOCUMENT_KINDS = Object.keys(KIND_LABEL) as DocumentKind[];
export const JOB_STAGE_TAGS = Object.keys(STAGE_TAG_LABEL) as JobStageTag[];
export const TECHNOLOGIES = Object.keys(TECHNOLOGY_LABEL) as Technology[];

// -------------------------------------------------------------------- send

/**
 * Both numbers are the API's, transcribed: the default from
 * `DEFAULT_LINK_EXPIRY_DAYS` in `documents/email.ts`, the ceiling from
 * `MAX_LINK_EXPIRY_DAYS` in `http/document-requests.ts`. They are repeated here
 * only so the form can say what will happen before the request is made; the API
 * remains the one that decides.
 */
export const DEFAULT_LINK_EXPIRY_DAYS = 30;
export const MAX_LINK_EXPIRY_DAYS = 365;

export const SEND_MODE_LABEL: Record<EmailSendMode, string> = {
  "secure-link": "A link that expires",
  attachment: "The file itself, attached",
};

/**
 * What choosing this mode actually commits the sender to.
 *
 * Written in the future tense and shown in the confirmation, because this is
 * the sentence that should make somebody stop: an attachment cannot be recalled
 * and cannot be expired, and that is a property of email rather than of this
 * software.
 */
export function describeSendMode(
  mode: EmailSendMode,
  linkExpiryDays: number,
): string {
  return mode === "attachment"
    ? "The document will be attached to the email. Once it has left it cannot be recalled, revoked or expired."
    : `The email will carry a link to the document, valid for ${linkExpiryDays} days from when the API sends it. The file itself is not attached, and the link can be allowed to expire.`;
}

/**
 * Addresses from one box, split the way people type them.
 *
 * Deliberately no validation beyond emptiness: the API validates every address
 * and names the one that is wrong by its position in the list, which is a
 * better message than anything invented here, and a second opinion about what
 * an email address looks like is a second thing to be wrong.
 */
export function parseRecipients(raw: string): string[] {
  return raw
    .split(/[,;\n]/)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

// -------------------------------------------------------------- signatures

export const SIGNATURE_MODE_LABEL: Record<SignatureMode, string> = {
  remote: "Remote — the signer follows a link",
  "on-site": "On site — signed on this device",
  internal: "Internal — a staff sign-off",
};

export const SIGNATURE_MODES = Object.keys(
  SIGNATURE_MODE_LABEL,
) as SignatureMode[];

/**
 * The envelope states, in the API's words.
 *
 * All five are here because all five exist in `envelope_status`, but two of
 * them cannot be reached from this portal today and the screen says so: there
 * is no route that marks an envelope sent, and none that records a decline.
 * Listing a state the software can produce next to one it cannot is exactly the
 * kind of quiet fiction these screens are meant to avoid, so the reachability
 * is recorded here rather than assumed.
 */
export const ENVELOPE_STATUS: Record<
  EnvelopeStatus,
  { readonly word: string; readonly meaning: string }
> = {
  draft: {
    word: "Outstanding",
    meaning:
      "Raised against the document's current bytes and waiting. Nothing has been sent to the signer: the API exposes no route that marks an envelope sent.",
  },
  sent: {
    word: "Sent",
    meaning: "Issued to the signer and awaiting their signature.",
  },
  signed: {
    word: "Signed",
    meaning: "A signature has been captured against these exact bytes.",
  },
  declined: { word: "Declined", meaning: "The signer refused." },
  voided: {
    word: "Voided",
    meaning: "Withdrawn. It can no longer be signed.",
  },
};

export function envelopeStatusWord(status: string): string {
  return ENVELOPE_STATUS[status as EnvelopeStatus]?.word ?? humanise(status);
}
